import type { BookingWorklistItem } from '@my/tms-api';
import type { FerryBooking } from '@my/ferry-api';
import { roadLegEnd, type CoordinatesSourceValue } from '@my/directory-api';

import type { CrossingCarrier } from '../../ferry/catalogue';
import { ferryRowStatus } from '../../ferry/status';
import { corridorLabel, isCustomsCrossing, needsFerry } from './crossing';
import { canonicalPort } from './ports';
import { portCoordinatesByName } from '../../../utils/portCoordinates';
import type { BookingRow, BookingStatus, Corridor, HorizonDay, RouteOption } from './mockData';
import { recommendRoute, type ResolvedPick } from './recommendRoute';

/** Minimal address shape the builder needs (resolved from Directory). */
export interface AddressLite {
  countryIsoCode?: string | null;
  city?: string | null;
  name?: string | null;
  /** Needed to measure the arrival-port-to-delivery road leg. */
  latitude?: number | null;
  longitude?: number | null;
  /** With `coordinatesSource`: sent with the leg so the route cache keys a Google place by its id. */
  id?: string | null;
  placeId?: string | null;
  coordinatesSource?: CoordinatesSourceValue | null;
}

export interface BuildContext {
  addresses: Map<string, AddressLite>;
  partyNames: Map<string, string>;
  cargoLabels: Map<string, string>;
  /** Reference "now" for the delivery-day offset (kept out of the pure builder). */
  today: Date;
  /**
   * Routed truck times for an (order, arrival port) leg, on the second pass.
   *
   * <p>
   * Absent on the first pass, which is what reports the legs worth routing —
   * see `roadLegsToRoute`. Keyed by order so one delivery's leg cannot be
   * handed to another's.
   * </p>
   */
  resolveRoad?: (orderId: string, portName: string) => { minutes: number; km: number } | null;
  /** My.Ferry bookings this app made, keyed by their externalReference (= TMS booking id). */
  ferryByBookingId?: Map<string, FerryBooking>;
  /** The organisation's route picks resolved against the merged catalogue, and its carriers by key. */
  picks: ResolvedPick[];
  carriersByKey: Map<string, CrossingCarrier>;
  /** Service tags that mark a load hazardous. */
  hazardousTagIds: Set<string>;
  /** Per cargo definition: the temperature setting (null = not controlled), the equipment keys, container or not. */
  cargo: Map<string, { temperatureC: number | null; equipmentKeys: string[]; container: boolean }>;
}

/** One arrival-port-to-delivery leg worth a routed answer. */
export interface BookingRoadLeg {
  orderId: string;
  portName: string;
  /** From the built-in port table — coordinates only (our own data). */
  from: { latitude: number; longitude: number };
  to: { latitude: number; longitude: number; placeId: string | null; addressId: string | null };
}

function dayOffset(deliveryDate: string | null | undefined, today: Date): number | null {
  if (!deliveryDate) return null;
  const d = new Date(`${deliveryDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((d.getTime() - startOfToday.getTime()) / 86_400_000);
}

function toHorizon(offset: number | null): HorizonDay {
  if (offset === null || offset <= 0) return 'today';
  if (offset === 1) return 'plus1';
  return 'plus2';
}

/** "HH:mm:ss" → "HH:mm" (TimeOnly from the API). */
function hhmm(time: string | null | undefined): string | null {
  return time ? time.slice(0, 5) : null;
}

/** "HH:mm" → minutes since midnight. */
function parseHM(s: string): number {
  const [h, m] = s.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/**
 * Booked through My.Ferry → the carrier's status decides; a booking with a
 * reference but no Ferry booking was made outside the integration (Manual);
 * one with neither is placed but unconfirmed.
 */
function statusOf(item: BookingWorklistItem, ferry: FerryBooking | null): BookingStatus {
  if (!item.hasBooking || !item.booking) return 'Unbooked';
  if (ferry) return ferryRowStatus(ferry);
  return item.booking.carrierReference ? 'Manual' : 'Booked';
}

/**
 * The route cell for a booked row, from the OrderBooking. Road km / cost / the
 * sailing type are the parked engine's job — left at 0 / default and suppressed
 * by the queue's route renderer until that lands.
 */
function bookedRoute(item: BookingWorklistItem, ctx: BuildContext): RouteOption | undefined {
  const b = item.booking;
  if (!b) return undefined;
  const departureTask = b.tasks.find((t) => t.type === 'BookingDeparture');
  const arrivalTask = b.tasks.find((t) => t.type === 'BookingArrival');
  const portName = (addressId: string | undefined) =>
    addressId ? (ctx.addresses.get(addressId)?.name ?? '') : '';
  return {
    routeShort: b.routeName ?? b.routeCode ?? '—',
    departurePort: portName(departureTask?.addressId) || undefined,
    arrivalPort: portName(arrivalTask?.addressId),
    operator: (b.carrierPartyId && ctx.partyNames.get(b.carrierPartyId)) || '',
    roadKm: 0,
    cost: 0,
    type: 'Off-peak',
    sailTime: hhmm(b.sailTime) ?? undefined,
  };
}

/**
 * Turn TMS booking-worklist items into the two queues. Only orders that cross a
 * sea (origin/destination on different landmasses — see `crossing`) make the
 * cut; the rest are domestic and ignored. The route recommendation
 * (`best`/`alts`/`cutoffMinutes`) for unbooked rows ranks the organisation's
 * own picks; the sailings come from the ferry service when the row opens.
 */
export function buildBookingRows(
  items: BookingWorklistItem[],
  ctx: BuildContext,
): { toBook: BookingRow[]; booked: BookingRow[] } {
  const toBook: BookingRow[] = [];
  const booked: BookingRow[] = [];

  for (const item of items) {
    const origin = item.originAddressId ? ctx.addresses.get(item.originAddressId) : undefined;
    const dest = item.destinationAddressId ? ctx.addresses.get(item.destinationAddressId) : undefined;
    const originCountry = origin?.countryIsoCode ?? null;
    const destCountry = dest?.countryIsoCode ?? null;

    // Bookings cockpit = sea crossings only.
    if (needsFerry(originCountry, destCountry) !== true) continue;

    const offset = dayOffset(item.deliveryDate, ctx.today);
    const ferry = item.booking ? (ctx.ferryByBookingId?.get(item.booking.id) ?? null) : null;
    const status = statusOf(item, ferry);
    const isBooked = status !== 'Unbooked';
    const customs = isCustomsCrossing(originCountry, destCountry) ?? false;
    const window = hhmm(item.deliveryTime) ?? 'Any';

    // Cutoff/feasibility context for the recommendation — all minutes from
    // today-midnight. Deadline = the delivery window (end-of-day when "Any");
    // "now" comes from ctx.today so a Rerun re-stamps the countdown.
    const deadlineMin = (offset ?? 0) * 1440 + (window === 'Any' ? 1439 : parseHM(window));
    const nowAbsMin = ctx.today.getHours() * 60 + ctx.today.getMinutes();

    // Unbooked crossing → myI suggests one of the organisation's own routes,
    // preferring one still catchable for the deadline, and returns its cutoff;
    // held only in this derived row, never persisted until booked.
    const recommendation = isBooked
      ? null
      : recommendRoute(originCountry, destCountry, ctx.picks, ctx.carriersByKey, {
          deadlineMin,
          nowAbsMin,
          customs,
          // Where the load is actually going, so the recommendation ranks ports
          // by their distance to THIS drop rather than by a per-route constant.
          destination: dest ?? null,
          resolveRoad: ctx.resolveRoad
            ? (portName) => ctx.resolveRoad!(item.orderId, portName)
            : undefined,
        });

    // Departure port drives the Port filter chips: the real booking leg's port
    // if one exists, else the recommended route's departure port.
    const departureAddr = item.departurePortAddressId
      ? ctx.addresses.get(item.departurePortAddressId)
      : undefined;
    // Canonicalise whichever port source we have — the real booking-leg address
    // (e.g. "Rosslare Harbour") or the recommended route's port — onto the
    // catalogue's canonical name ("Rosslare Europort") so one physical port = one chip.
    const rawDeparturePort =
      departureAddr?.city ?? departureAddr?.name ?? recommendation?.best.departurePort ?? null;
    const departurePort = rawDeparturePort ? canonicalPort(rawDeparturePort) : 'unassigned';
    const cargo = ctx.cargo.get(item.cargoDefinitionId);

    const row: BookingRow = {
      id: item.orderId,
      orderNumber: item.orderNumber,
      customer: ctx.partyNames.get(item.customerPartyId) ?? 'Customer',
      cargoLabel: ctx.cargoLabels.get(item.cargoDefinitionId) ?? 'Cargo',
      destination: dest?.city ?? dest?.name ?? '—',
      corridor: (corridorLabel(originCountry, destCountry) ?? '—') as Corridor,
      customs,
      window,
      status,
      bookingRef: item.booking?.carrierReference ?? null,
      booking: item.booking ?? null,
      ferry,
      best: isBooked ? bookedRoute(item, ctx) : recommendation?.best,
      alts: isBooked ? [] : (recommendation?.alts ?? []),
      // Only flagged when there's genuinely no route on file for the crossing.
      noFitReason: isBooked || recommendation ? undefined : 'No route on file for this crossing',
      // Minutes until the recommended sailing's port cutoff (≤0 → "Missed");
      // undefined for booked rows / no route → "—". Recomputed on Rerun (ctx.today).
      cutoffMinutes: recommendation?.cutoffMinutes,
      horizon: toHorizon(offset),
      deliveryDayOffset: offset ?? 0,
      portId: departurePort,
      hazardous: item.serviceTagIds.some((id) => ctx.hazardousTagIds.has(id)),
      temperatureC: cargo?.temperatureC ?? null,
      equipmentKeys: cargo?.equipmentKeys ?? [],
      containerCargo: cargo?.container ?? false,
    };

    (isBooked ? booked : toBook).push(row);
  }

  return { toBook, booked };
}

/**
 * The legs worth routing for a set of rows: each unbooked row's RECOMMENDED
 * arrival port to its delivery.
 *
 * <p>
 * The recommendation only, not every alternative. A worklist of thirty rows
 * times twelve lanes is a metered API call per lane per row for numbers nobody
 * is reading; the shortlist is what the dispatcher sees, and the alternatives
 * stay estimates until one is chosen. Deduplicated, because a depot sending ten
 * loads through the same port to the same customer is one leg.
 * </p>
 */
export function roadLegsToRoute(
  rows: readonly BookingRow[],
  addresses: Map<string, AddressLite>,
  destinationAddressIdOf: (orderId: string) => string | null | undefined,
): BookingRoadLeg[] {
  const seen = new Set<string>();
  const out: BookingRoadLeg[] = [];
  for (const row of rows) {
    const portName = row.best?.arrivalPort;
    if (!portName) continue;
    const port = portCoordinatesByName(portName);
    if (!port) continue;
    const addressId = destinationAddressIdOf(row.id);
    const dest = addressId ? addresses.get(addressId) : undefined;
    const to = dest ? roadLegEnd(dest) : null;
    if (!to) continue;

    const key = `${portName}>${to.placeId ?? `${to.latitude.toFixed(3)},${to.longitude.toFixed(3)}`}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      orderId: row.id,
      portName,
      from: port,
      to,
    });
  }
  return out;
}
